/*
---------------------------------------------------------
Copyright © 2026 Daniel Woik (DW E-Commerce). All rights reserved.
Custom-coded by DW E-Commerce. This code is licensed for use on
this store only, as part of the DW E-Commerce optimization service.
Copying, reselling, redistributing or using it on any other store
is not permitted without written permission.
---------------------------------------------------------
DW Kit:   Product Card (slider)
Version:  1.5.0
Updated:  2026-10-16 — Arrows in a section's heading row: controls with data-dw-slider-controls drive the visible slider inside the element they name.
Source:   DW-Component-Library/Kits/Product-Card

FUNCTION LOCKED. Needs assets/dw-scroller.js (loaded first).
<dw-slider> works like the slideshow of Shopify's Horizon theme:
  - Touch: the browser's own scrolling with mandatory snapping. The row
    follows the finger and only snaps to a card when the finger lets go.
  - Mouse: drag with the left button. Snapping is off while dragging;
    on release the row moves to the nearest card, one card further in
    the drag direction after a quick flick. A drag never counts as a
    click on the card under the mouse.
  - Arrows: one page of fully visible cards per click, disabled at the
    start and at the end, hidden when the row does not scroll. They sit
    in the heading row: inside the slider, or in the section's own
    heading (snippets/dw-row-controls.liquid).
  - Which cards are visible is tracked with an IntersectionObserver
    (70 % visible counts), the current card follows the user's scroll.
  - Scrolling is only switched on while the row is near the viewport
    (prevents iOS Safari crashes from too many scroll layers).
  - A dw-slider inside another dw-slider or inside any sideways row, for
    example a theme carousel (image swipe in a card inside a product row),
    stays off and is no scroll box, so the row gets every swipe.

Markup:
  <dw-slider class="dw-slider">
    [heading row with buttons data-dw-slider-prev / data-dw-slider-next]
    <div class="dw-slider__track" data-dw-slider-track>
      <div class="dw-slider__item">...</div> ...
    </div>
  </dw-slider>
Arrows outside the slider (a section's heading row):
  <div data-dw-slider-controls="#section-id"> ... the same two buttons ... </div>
  They drive the first visible dw-slider inside the element the selector
  names (or that element itself), so tabs that show one row at a time
  need no extra script. The element gets [no-scroll] like the slider.
After changing the items by script, call element.refresh().
---------------------------------------------------------
*/
(function () {
  if (window.customElements.get('dw-slider')) return;

  var VISIBLE_THRESHOLD = 0.7;

  /* One shared observer: scrolling is only enabled near the viewport. */
  var viewport = null;
  function viewportObserver() {
    if (!viewport && 'IntersectionObserver' in window) {
      viewport = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          entry.target.toggleAttribute('in-viewport', entry.isIntersecting);
        });
      }, { rootMargin: '100px' });
    }
    return viewport;
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function centerOf(el) {
    var rect = el.getBoundingClientRect();
    return rect.left + rect.width / 2;
  }

  function preventClick(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  /* Arrows outside a slider: the first visible slider inside the element the controls name. */
  function controlsTarget(controls) {
    var scope = null;
    try {
      scope = document.querySelector(controls.getAttribute('data-dw-slider-controls'));
    } catch (e) {
      return null;
    }
    if (!scope) return null;
    if (scope.matches('dw-slider')) return scope;
    var sliders = scope.querySelectorAll('dw-slider:not([data-dw-slider-gallery]):not([disabled])');
    for (var i = 0; i < sliders.length; i++) {
      if (sliders[i].getClientRects().length && getComputedStyle(sliders[i]).visibility !== 'hidden') return sliders[i];
    }
    return null;
  }

  document.addEventListener('click', function (event) {
    if (!(event.target instanceof Element)) return;
    var button = event.target.closest('[data-dw-slider-prev], [data-dw-slider-next]');
    var controls = button && button.closest('[data-dw-slider-controls]');
    if (!controls) return;
    var slider = controlsTarget(controls);
    if (!slider || typeof slider.next !== 'function') return;
    event.preventDefault();
    if (button.hasAttribute('data-dw-slider-next')) slider.next();
    else slider.previous();
  });

  class DWSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-dw-slider-track]');
      if (!this.track) return;

      var observer = viewportObserver();
      if (observer) observer.observe(this);
      else this.setAttribute('in-viewport', '');

      if (this._ready) {
        this.refresh();
        return;
      }
      this._ready = true;
      this._current = 0;
      this._visible = [];
      this._dragging = false;

      if (this.isNested) {
        /* Like Horizon: a slider inside another slider stays still. */
        this.setAttribute('disabled', '');
        return;
      }

      this._onArrow = this.onArrow.bind(this);
      this.addEventListener('click', this._onArrow);

      this._onMouseDown = this.onMouseDown.bind(this);
      this.track.addEventListener('mousedown', this._onMouseDown);
      /* Touch screens also send a mousedown after a tap; that is not a mouse drag. */
      this._lastTouch = 0;
      this.track.addEventListener('touchstart', () => { this._lastTouch = Date.now(); }, { passive: true });

      this._scroll = new window.DWScroller(this.track, {
        onScroll: this.onUserScroll.bind(this),
        onScrollStart: () => this.setAttribute('transitioning', ''),
        onScrollEnd: () => {
          this.removeAttribute('transitioning');
          this.updateControls();
        }
      });

      this.observeSlides();

      if ('ResizeObserver' in window) {
        this._resize = new ResizeObserver(() => this.updateControls());
        this._resize.observe(this.track);
      }

      this.addEventListener('pointerenter', () => this.setAttribute('actioned', ''), { once: true });
      requestAnimationFrame(() => this.updateControls());
    }

    disconnectedCallback() {
      if (viewport) viewport.unobserve(this);
    }

    /* Inside another dw-slider or any sideways row (a theme carousel, for example):
       the row must get every horizontal swipe, so this slider stays off. */
    get isNested() {
      var parent = this.parentElement;
      if (!parent) return false;
      if (parent.closest('dw-slider')) return true;
      for (var el = parent; el && el !== document.body; el = el.parentElement) {
        var style = getComputedStyle(el);
        /* A sideways row snaps on x, or scrolls on x only; a panel that scrolls up and down is no row. */
        if (/x|inline|both/.test(style.scrollSnapType)) return true;
        if (/^(auto|scroll)$/.test(style.overflowX) && /^(hidden|clip)$/.test(style.overflowY)) return true;
      }
      return false;
    }

    get slides() {
      return Array.prototype.filter.call(this.track.children, function (el) {
        return el.classList.contains('dw-slider__item') && !el.hidden;
      });
    }

    get current() {
      return this._current;
    }

    set current(value) {
      this._current = value;
      this.updateControls();
    }

    get visibleCount() {
      return Math.max(1, this._visible.length);
    }

    /* Buttons of this slider only, never of a slider nested inside it. */
    ownButtons(selector) {
      return Array.prototype.filter.call(this.querySelectorAll(selector), (button) => button.closest('dw-slider') === this);
    }

    observeSlides() {
      if (this._io) this._io.disconnect();
      this._visible = [];
      if (!('IntersectionObserver' in window)) return;
      this._io = new IntersectionObserver((entries) => {
        var slides = this.slides;
        entries.forEach((entry) => {
          var slide = entry.target;
          var index = this._visible.indexOf(slide);
          var visible = entry.intersectionRatio >= VISIBLE_THRESHOLD;
          if (visible && index === -1) this._visible.push(slide);
          if (!visible && index > -1) this._visible.splice(index, 1);
        });
        this._visible.sort(function (a, b) { return slides.indexOf(a) - slides.indexOf(b); });
        this.updateControls();
      }, { root: this.track, threshold: VISIBLE_THRESHOLD, rootMargin: '1px' });
      this.slides.forEach((slide) => this._io.observe(slide));
    }

    /* Index of the card closest to the start (several visible) or to the middle (one visible). */
    sync() {
      var slides = this.slides;
      var visible = this._visible.filter(function (s) { return slides.indexOf(s) > -1; });
      if (!visible.length) return this._current;
      var rect = this.track.getBoundingClientRect();
      var padding = parseFloat(getComputedStyle(this.track).paddingInlineStart) || 0;
      var reference = visible.length > 1 ? rect.left + padding : rect.left + rect.width / 2;
      var best = visible[0];
      var bestDistance = Infinity;
      visible.forEach(function (slide) {
        var point = visible.length > 1 ? slide.getBoundingClientRect().left : centerOf(slide);
        var distance = Math.abs(point - reference);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = slide;
        }
      });
      this.current = slides.indexOf(best);
      return this._current;
    }

    onUserScroll() {
      this.sync();
    }

    select(index, options) {
      var slides = this.slides;
      if (!slides.length || !this._scroll) return;
      index = clamp(index, 0, slides.length - 1);
      this._scroll.to(slides[index], options);
      this.current = index;
    }

    next() {
      this.select(this._current + this.visibleCount);
    }

    previous() {
      this.select(this._current - this.visibleCount);
    }

    onArrow(event) {
      var button = event.target.closest('[data-dw-slider-prev], [data-dw-slider-next]');
      if (!button || button.closest('dw-slider') !== this) return;
      event.preventDefault();
      event.stopPropagation();
      if (button.hasAttribute('data-dw-slider-next')) this.next();
      else this.previous();
    }

    updateControls() {
      var track = this.track;
      if (!track) return;
      var max = track.scrollWidth - track.clientWidth;
      var scrollable = max > 2;
      this.toggleAttribute('no-scroll', !scrollable);
      var atStart = !scrollable || track.scrollLeft <= 2 || this._current === 0 && track.scrollLeft <= 2;
      var atEnd = !scrollable || track.scrollLeft >= max - 2;
      this.ownButtons('[data-dw-slider-prev]').forEach(function (b) { b.disabled = atStart; });
      this.ownButtons('[data-dw-slider-next]').forEach(function (b) { b.disabled = atEnd; });
      /* Arrows in the section's heading row follow the slider that is visible now. */
      Array.prototype.forEach.call(document.querySelectorAll('[data-dw-slider-controls]'), (controls) => {
        if (controlsTarget(controls) !== this) return;
        controls.toggleAttribute('no-scroll', !scrollable);
        controls.querySelectorAll('[data-dw-slider-prev]').forEach(function (b) { b.disabled = atStart; });
        controls.querySelectorAll('[data-dw-slider-next]').forEach(function (b) { b.disabled = atEnd; });
      });

      /* Arrows on product rows sit in the middle of the card images. */
      var media = track.querySelector('.dw-pcard__media');
      if (media && !this.hasAttribute('data-dw-slider-gallery')) {
        var top = media.getBoundingClientRect().top - this.getBoundingClientRect().top + media.offsetHeight / 2;
        this.style.setProperty('--dw-slider-arrow-top', top + 'px');
      }
    }

    refresh() {
      if (!this.track || this.hasAttribute('disabled')) return;
      this.track.scrollLeft = 0;
      this._current = 0;
      this.observeSlides();
      requestAnimationFrame(() => this.updateControls());
    }

    /* Mouse drag, the same way Horizon does it. */
    onMouseDown(event) {
      var slides = this.slides;
      if (event.button !== 0 || slides.length <= 1 || this._dragging) return;
      if (Date.now() - this._lastTouch < 1000) return;
      if (event.sourceCapabilities && event.sourceCapabilities.firesTouchEvents) return;
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('button, input, select, textarea, label, model-viewer')) return;

      event.preventDefault();
      var self = this;
      var startX = event.clientX;
      var previousX = startX;
      var startTime = performance.now();
      var velocity = 0;
      var moved = false;
      var travelled = 0;
      var startIndex = this._current;
      var controller = new AbortController();
      var signal = controller.signal;

      this._dragging = true;
      this._scroll.snap = false;

      function onMove(moveEvent) {
        var x = moveEvent.clientX;
        if (x === startX && !moved) return;
        if (!moved) {
          moved = true;
          try { self.setPointerCapture(moveEvent.pointerId); } catch (e) {}
          document.addEventListener('click', preventClick, { capture: true, once: true, signal: signal });
          self.setAttribute('dragging', '');
        }
        var delta = previousX - x;
        var elapsed = performance.now() - startTime || 1;
        velocity = Math.round((delta / elapsed) * 1000);
        previousX = x;
        travelled += Math.abs(delta);
        self._scroll.by(delta, { instant: true });
      }

      function onUp(upEvent) {
        controller.abort();
        self._dragging = false;
        var all = self.slides;
        if (!all.length) return;
        var direction = Math.sign(velocity);
        var nearest = self.sync();
        var step = nearest !== startIndex || Math.abs(velocity) < 10 || travelled < 10 ? 0 : direction;
        var target = clamp(nearest + step, 0, all.length - 1);
        self._scroll.to(all[target]);
        self.removeAttribute('dragging');
        try { if (upEvent && upEvent.pointerId !== undefined) self.releasePointerCapture(upEvent.pointerId); } catch (e) {}
        self.current = target;
        /* A click right after a drag without movement must still work. */
        if (!moved) document.removeEventListener('click', preventClick, { capture: true });
        self._scroll.finished.then(function () {
          if (self._dragging) return;
          self._scroll.snap = true;
        });
      }

      document.addEventListener('pointermove', onMove, { signal: signal });
      document.addEventListener('pointerup', onUp, { signal: signal });
      document.addEventListener('pointercancel', onUp, { signal: signal });
      document.addEventListener('pointercapturelost', onUp, { signal: signal });
      /* A new press always ends an unfinished drag first (same safeguard as Horizon). */
      document.addEventListener('pointerdown', onUp, { signal: signal });
    }
  }

  window.customElements.define('dw-slider', DWSlider);
})();
